import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { InputWithConfirm, RadioOptionsWithConfirm, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../store';
import QuestionPage from '../../../templates/question';
import OtherNames from './otherNames';

import { IOtherName } from '../../../../types';
import { Q } from '../../../../store/student/types';
import { savePI } from '../../../../store/student/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { createUpdatePIFieldAction } from '../../../../store/student/personalInfo/actions';
import { selectPage1Data, selectPersonalInfo } from './utils/selectors';
import { getPageStatus } from './utils';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IDispatchProps {
  onNext: (config: any) => void;
  onChange: (
    fieldName: string,
    value: string | number | boolean | undefined | Array<IOtherName>,
    refValue?: any,
  ) => void;
  onBackToTaskList: () => void;
}

const CURRENT_STEP = 1;
const STEPS = 10;

const PersonalQPage1: React.FC = () => {
  const dispatch = useDispatch();

  const onNext = (config: any) => {
    dispatch(savePI(Pages.PAGE1, config));
  };
  const onChange = useCallback(
    (fieldName: string, value: any, refValue?: any): void => {
      dispatch(createUpdatePIFieldAction(fieldName, value, refValue));
    },
    [dispatch],
  );

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  const { givenNameConfig, middleNameConfig, surnameConfig, hasOtherNamesConfig } = useSelector(
    selectPage1Data,
    shallowEqual,
  );
  const {
    surname = surnameConfig?.defaultValue as string,
    middleName = middleNameConfig?.defaultValue as string,
    givenName = givenNameConfig?.defaultValue as string,
    hasOtherNames = hasOtherNamesConfig?.defaultValue as boolean,
    otherNames: storeOtherNames = [],
  } = useSelector(selectPersonalInfo, shallowEqual);
  const { config, importData, isPageComplete, sectionTitle } = useSelector(
    (state: ApplicationState) => ({
      config: state.student.config,
      importData: state.student.importData,
      isPageComplete: state.student.offer.isPIPage1Complete,
      sectionTitle: state.student.config.Checklist.PersonalInfo.shortTitle,
    }),
    shallowEqual,
  );
  const {
    complete,
    visible: {
      surname: surnameVisible,
      middleName: middleNameVisible,
      givenName: givenNameVisible,
      hasOtherNames: hasOtherNamesVisible,
    },
  } = useMemo(
    () =>
      getPageStatus(config, { surname, middleName, givenName, hasOtherNames, otherNames: storeOtherNames }, importData),
    [surname, middleName, givenName, hasOtherNames, storeOtherNames, config, importData],
  );

  const getImportApprovedFields = useCallback((): Array<Q> => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData?.givenName === givenName) {
        importApproved.push(Q.GIVEN_NAME);
      }
      if (importData?.otherName === middleName) {
        importApproved.push(Q.MIDDLE_NAME);
      }
      if (importData?.familyName === surname) {
        importApproved.push(Q.SURNAME);
      }
    }
    return importApproved;
  }, [isPageComplete, importData, givenName, middleName, surname]);

  const getImportUpdateFields = useCallback((): Array<Q> => {
    const importUpdateEnabled = [];
    if (isPageComplete) {
      if (importData.givenName && givenName !== importData.givenName) {
        importUpdateEnabled.push(Q.GIVEN_NAME);
      }
      if (importData.otherName && middleName !== importData.otherName) {
        importUpdateEnabled.push(Q.MIDDLE_NAME);
      }
      if (importData.familyName && surname !== importData.familyName) {
        importUpdateEnabled.push(Q.SURNAME);
      }
    }
    return importUpdateEnabled;
  }, [isPageComplete, importData, givenName, middleName, surname]);

  const [importEdited, setImportEdited] = useState<string[]>([]);
  const [showModal, setShowModal] = useState([...storeOtherNames.map(_ => false), false]);
  const [otherNames, setOtherNames] = useState([...storeOtherNames, { surname: '', givenName: '', reason: '' }]);
  const [importApproved, setImportApproved] = useState(getImportApprovedFields());
  const [importUpdateEnabled] = useState(getImportUpdateFields());

  useEffect(() => {
    if (JSON.stringify(otherNames) !== JSON.stringify(storeOtherNames)) {
      setOtherNames([...storeOtherNames, { surname: '', givenName: '', reason: '' }]);
      setShowModal([...storeOtherNames.map(_ => false), false]);
    }
  }, [storeOtherNames]);

  const toggleModal = useCallback(
    (index: number) => {
      if (showModal[index] !== undefined) {
        setShowModal(currentShowModal => {
          const newShowModal = [...currentShowModal];
          newShowModal[index] = !currentShowModal[index];
          return newShowModal;
        });
      }
    },
    [showModal],
  );

  const deleteOtherName = useCallback(
    (index: number): void => {
      onChange(Q.OTHER_NAMES, [...storeOtherNames.slice(0, index), ...storeOtherNames.slice(index + 1)]);
    },
    [storeOtherNames, onChange],
  );

  const isImportEnabled = useCallback(
    (target: Q): boolean => {
      switch (target) {
        case Q.SURNAME:
          return surnameVisible && !!importData.familyName;
        case Q.MIDDLE_NAME:
          return middleNameVisible && !!importData.otherName;
        case Q.GIVEN_NAME:
          return givenNameVisible && !!importData.givenName;
        default:
          return false;
      }
    },
    [surnameVisible, middleNameVisible, givenNameVisible, importData],
  );

  const isImportUpdateEnabled = useCallback(
    (target: Q): boolean => {
      if (!isPageComplete) return false;
      switch (target) {
        case Q.SURNAME:
          return !!surname && importUpdateEnabled.includes(target);
        case Q.MIDDLE_NAME:
          return !!middleName && importUpdateEnabled.includes(target);
        case Q.GIVEN_NAME:
          const bVal = !!givenName && importUpdateEnabled.includes(target);
          return bVal;
        default:
          return false;
      }
    },
    [surname, middleName, givenName, importUpdateEnabled, isPageComplete],
  );

  const handleImportApprove = (target: Q): void => {
    setImportApproved(state => [...state, target]);
    setImportEdited(state => state.filter(q => q !== target));
  };
  const handleImportEdit = (target: Q): void => {
    setImportApproved(state => state.filter(q => q !== target));
    setImportEdited(state => [...state, target]);
  };
  const handleImportUndo = (target: Q): void => {
    setImportApproved(state => state.filter(q => q !== target));
    setImportEdited(state => state.filter(q => q !== target));
  };
  const handleYesNoClick = (target: Q, question: any): void => {
    onChange(target, question.char === 'Y');
  };

  return (
    <QuestionPage
      section={sectionTitle}
      subtitle="All names must match your passport exactly."
      complete={complete}
      currentStep={CURRENT_STEP}
      steps={STEPS}
      onNext={() => onNext(config)}
      onBackToTaskList={onBackToTaskList}
    >
      {givenNameVisible && (
        <InputWithConfirm
          key={Q.GIVEN_NAME}
          id={Q.GIVEN_NAME}
          value={givenName}
          label={`${givenNameConfig?.label}${!middleNameVisible ? 's' : ''}`}
          inputHelperText={givenNameConfig?.helpText}
          onChange={(evt: any) => onChange(Q.GIVEN_NAME, evt.currentTarget.value)}
          autoFocus={true}
          importEnabled={isImportEnabled(Q.GIVEN_NAME)}
          importValue={importData.givenName}
          importUpdateEnabled={isImportUpdateEnabled(Q.GIVEN_NAME)}
          importApproved={importApproved.includes(Q.GIVEN_NAME)}
          importEdited={importEdited.includes(Q.GIVEN_NAME)}
          onImportApprove={() => {
            handleImportApprove(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, importData.givenName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, isImportUpdateEnabled(Q.GIVEN_NAME) ? givenName : importData.givenName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, isImportUpdateEnabled(Q.GIVEN_NAME) ? givenName : '');
          }}
        />
      )}

      {middleNameVisible && (
        <InputWithConfirm
          key={Q.MIDDLE_NAME}
          id={Q.MIDDLE_NAME}
          value={middleName}
          label={middleNameConfig?.label}
          inputHelperText={middleNameConfig?.helpText}
          onChange={(evt: any) => onChange(Q.MIDDLE_NAME, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.MIDDLE_NAME)}
          importValue={importData.otherName}
          importUpdateEnabled={isImportUpdateEnabled(Q.MIDDLE_NAME)}
          importApproved={importApproved.includes(Q.MIDDLE_NAME)}
          importEdited={importEdited.includes(Q.MIDDLE_NAME)}
          onImportApprove={() => {
            handleImportApprove(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, importData.otherName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, isImportUpdateEnabled(Q.MIDDLE_NAME) ? middleName : importData.otherName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, isImportUpdateEnabled(Q.MIDDLE_NAME) ? middleName : '');
          }}
        />
      )}

      {surnameVisible && (
        <InputWithConfirm
          key={Q.SURNAME}
          id={Q.SURNAME}
          value={surname}
          label={surnameConfig?.label}
          inputHelperText={surnameConfig?.helpText}
          onChange={(evt: any) => onChange(Q.SURNAME, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.SURNAME)}
          importValue={importData.familyName}
          importUpdateEnabled={isImportUpdateEnabled(Q.SURNAME)}
          importApproved={importApproved.includes(Q.SURNAME)}
          importEdited={importEdited.includes(Q.SURNAME)}
          onImportApprove={() => {
            handleImportApprove(Q.SURNAME);
            onChange(Q.SURNAME, importData.familyName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.SURNAME);
            onChange(Q.SURNAME, isImportUpdateEnabled(Q.SURNAME) ? surname : importData.familyName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.SURNAME);
            onChange(Q.SURNAME, isImportUpdateEnabled(Q.SURNAME) ? surname : '');
          }}
        />
      )}

      {hasOtherNamesVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_OTHER_NAMES}
          id={Q.HAS_OTHER_NAMES}
          value={hasOtherNames}
          label={hasOtherNamesConfig?.label}
          inputHelperText={hasOtherNamesConfig?.helpText}
          onClick={(question: any) => handleYesNoClick(Q.HAS_OTHER_NAMES, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOtherNames === true,
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOtherNames === false,
            },
          ]}
        />
      )}

      {hasOtherNames && (
        <CardContainer title={`Alternative or Previous Names`}>
          {otherNames.map((otherName: IOtherName, index: number) => (
            <CardWithModal
              key={index}
              id={`other-name-${index}`}
              index={index}
              modalTitle="Add an Alternative or Previous Name"
              modalBlurb="Please enter this exactly as it appeared on previous passports/ID cards"
              cardItems={[
                {
                  label: 'Given Names',
                  value: otherName.givenName,
                },
                {
                  label: 'Family Name',
                  value: otherName.surname,
                },
                {
                  label: 'Name Reason',
                  value: otherName.reason,
                },
              ]}
              onRemove={deleteOtherName}
              addButtonLabel="Add Alternative or Previous Name"
              addAnotherButtonLabel="I have another name to add"
              hasAddMoreButton={otherName.givenName === '' && otherName.surname === '' && otherName.reason === ''}
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <OtherNames
                index={index}
                givenName={otherName.givenName}
                surname={otherName.surname}
                reason={otherName.reason}
                onSaveAndClose={(otherName: IOtherName) => {
                  const _otherNames = [...storeOtherNames];
                  _otherNames[index] = {
                    ...otherName,
                  };
                  onChange(Q.OTHER_NAMES, _otherNames);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};

export default PersonalQPage1;
